import { useState, useRef, useEffect } from 'react'
import axios from 'axios'
import { useSelector } from 'react-redux'
import FeatureGate from '../FeaturesGate.js'
import usePlan from '../../hooks/usePlan.js'
import AlertMessage from '../layout/AlertMessage.js'
import { getPlanById } from '../../Plan.js'
import RoutineEntry from '../chat/RoutineEntry.js'

import {
  FiCamera,
  FiDroplet,
  FiMapPin,
  FiPackage,
  FiScissors,
  FiUser,
  FiMessageCircle,
} from 'react-icons/fi'
import { ShareButtons } from '../shareable_feature/ShareCard.js'
import './userProfile.css'
// ─── CSS ──────────────────────────────────────────────────────────────────────

// ─── Question metadata ────────────────────────────────────────────────────────
const QUESTION_META = [
  { icon: FiUser, key: 'shoppingFor' },
  { icon: FiMapPin, key: 'profile' },
  { icon: FiCamera, key: 'hairPhoto' },
  { icon: FiScissors, key: 'hairTypeAndConcerns' },
  { icon: FiDroplet, key: 'currentRoutine' },
]

const TABS = [
  { id: 'overview', key: 'overview' },
  { id: 'products', key: 'products' },
  { id: 'diagnostics', key: 'diagnostics' },
  { id: 'routine', key: 'routine' },
  { id: 'tracker', key: 'tracker' },
]

const DEFAULT_CHECKLIST = [
  {
    id: 'c1',
    label: 'Pre-poo treatment',
    sub: 'Warm oil blend 30 min before washing',
    freq: 'Weekly',
    done: false,
  },
  {
    id: 'c2',
    label: 'Gentle cleanse',
    sub: 'Sulphate-free shampoo, focus on scalp',
    freq: 'Weekly',
    done: false,
  },
  {
    id: 'c3',
    label: 'Deep condition',
    sub: 'Heat mask 20–30 min, detangle wet hair',
    freq: 'Weekly',
    done: false,
  },
  {
    id: 'c4',
    label: 'LOC method',
    sub: 'Liquid → Oil → Cream on damp hair',
    freq: 'After wash',
    done: false,
  },
  {
    id: 'c5',
    label: 'Scalp massage',
    sub: '5 min with fingertips or scalp brush',
    freq: '3× per week',
    done: false,
  },
  {
    id: 'c6',
    label: 'Protective styling',
    sub: 'Braids, twists, or buns for retention',
    freq: 'As needed',
    done: false,
  },
  {
    id: 'c7',
    label: 'Moisture check',
    sub: 'Spritz water mist on dry sections',
    freq: 'Daily',
    done: false,
  },
  {
    id: 'c8',
    label: 'Silk scarf at night',
    sub: 'Protect style and retain moisture',
    freq: 'Nightly',
    done: false,
  },
]

const WEEK_DAYS = ['M', 'T', 'W', 'T', 'F', 'S', 'S']

const buildMonthCalendar = (year, month) => {
  const today = new Date()
  const firstDay = new Date(year, month, 1)
  const lastDay = new Date(year, month + 1, 0)
  const startDow = (firstDay.getDay() + 6) % 7
  const days = []

  for (let i = 0; i < startDow; i++) days.push({ day: null, status: 'empty' })

  for (let d = 1; d <= lastDay.getDate(); d++) {
    const date = new Date(year, month, d)
    const isToday =
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    const isPast =
      date < new Date(today.getFullYear(), today.getMonth(), today.getDate())
    const isFuture =
      date > new Date(today.getFullYear(), today.getMonth(), today.getDate())
    days.push({
      day: d,
      date: date.toISOString().split('T')[0],
      isToday,
      isPast,
      isFuture,
      status: isFuture ? 'future' : isToday ? 'today' : 'none',
    })
  }
  return days
}

// ─── Helper: extract readable value from a collected answer entry ─────────────
const extractAnswerText = (entry) => {
  if (!entry) return null
  const val = entry.extractedValue ?? entry.value ?? entry.text ?? null
  if (val == null) return null
  if (typeof val === 'string') return val
  if (typeof val === 'object') {
    // e.g. demographics: { age: '26', gender: 'Female', location: 'Lagos' }
    return Object.values(val).filter(Boolean).join(' · ')
  }
  return String(val)
}

// ─── Helper: extract tags from an answer entry ────────────────────────────────
const extractTags = (entry) => {
  if (!entry) return []
  const val = entry.extractedValue ?? entry.value ?? null
  if (!val) return []
  if (typeof val === 'object' && !Array.isArray(val)) {
    return Object.values(val).filter((v) => v && typeof v === 'string')
  }
  if (Array.isArray(val)) return val.filter(Boolean)
  return []
}

const buildRoutineEntry = (routineText) => {
  if (!routineText) return null

  const cleanText = String(routineText)
    .replace(/\*\*/g, '')
    .replace(/\r/g, '')
    .trim()

  if (!cleanText) return null

  const blocks = cleanText
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean)

  const steps = blocks.flatMap((block, index) => {
    const lines = block
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)

    if (!lines.length) return []

    const titleLine = lines[0].replace(/^[-•*]\s*/, '')
    const cleanedTitle = titleLine.replace(/^\d+[\.)]\s*/, '')
    const detail = lines
      .slice(1)
      .map((line) => line.replace(/^[-•*]\s*/, ''))
      .join(' ')
      .trim()

    return [
      {
        step: `${index + 1}`,
        title: cleanedTitle || `Step ${index + 1}`,
        detail:
          detail || 'Follow this step as part of your personalised routine.',
        freq: 'Personalised',
      },
    ]
  })

  if (!steps.length) return null

  return {
    title: 'Your personalised routine',
    date: 'Generated from your profile',
    steps: steps.slice(0, 6),
  }
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function UserProfile({ goToPricing, onBack }) {
  // ── Pull real data from Redux ────────────────────────────────────────────────
  const agentData = useSelector((state) => state.agentData)
  const collectedAnswers =
    useSelector((state) => state.chatCollectedAnswers) || []
  const productRecommendations = useSelector(
    (state) => state.productRecommendations,
  )
  const diagnosticsLLMText =
    useSelector((state) => state.diagnosticsLLMText) || ''
  const routineAdviceText =
    useSelector((state) => state.chatProductRoutineData) || ''
  const routineAdviceReady =
    useSelector((state) => state.chatProductRoutineReady) || false
  const chatMessages = useSelector((state) => state.chatMessages) || []

  const segmentedImageRaw =
    useSelector((state) => state.segmentedRawImage) || null

  // ── Derive user info ─────────────────────────────────────────────────────────
  const userName = agentData?.agentFullName || 'User'
  const userAvatar = agentData?.agentProfilePicture || null

  // ── Derive hair type from collected answers ───────────────────────────────────
  const hairTypeAnswer = collectedAnswers.find(
    (a) =>
      a.stepId === 'predictedHairClass' ||
      a.saveKey === 'predictedHairClass' ||
      a.stepId === 'correctedHairClass',
  )
  const hairType =
    hairTypeAnswer?.value ||
    hairTypeAnswer?.extractedValue ||
    agentData?.hairType ||
    '—'

  // ── Derive hair photo from chat messages (the segmented image sent by AI) ────
  const hairPhotoMessage = chatMessages.find(
    (m) => m.from === 'ai' && m.photoUrl,
  )
  const hairPhoto = hairPhotoMessage?.photoUrl || null

  const hasProducts = productRecommendations?.product_name?.length > 0
  const [productImages, setProductImages] = useState({})
  const [imagesLoading, setImagesLoading] = useState(false)

  //For product image fetching from Azure Blob Storage
  const SAS_TOKEN =
    'sv=2024-11-04&ss=bfqt&srt=sco&sp=rwdlacupiytfx&se=2027-03-01T20:08:03Z&st=2026-03-25T11:53:03Z&spr=https,http&sig=TygbDO5J47eNr7ajUYXfT2ta4TOYsd2SlWNmFMQsz6U%3D'
  const BLOB_BASE =
    'https://halcosgwcblobv1.blob.core.windows.net/halcosgwcproductimages'

  // ── Build answers array for Overview tab ────────────────────────────────────
  // Map each question step to its display entry
  const STEP_TO_INDEX = {
    shoppingFor: 0,
    demographics: 1,
    hairPhoto: 2,
    hairProfile: 3,
    routinePreferences: 4,
  }

  const buildAnswers = () => {
    const answers = []

    const stepsToShow = [
      { saveKey: 'shoppingFor', questionIndex: 0 },
      { saveKey: 'demographics', questionIndex: 1 },
      { saveKey: 'hairPhoto', questionIndex: 2 },
      { saveKey: 'hairProfile', questionIndex: 3 },
      { saveKey: 'routinePreferences', questionIndex: 4 },
    ]

    stepsToShow.forEach(({ saveKey, questionIndex }) => {
      const entry = collectedAnswers.find(
        (a) => a.saveKey === saveKey || a.stepId === saveKey,
      )
      if (!entry) return

      if (saveKey === 'hairPhoto') {
        // Show hair photo
        const photoUrl = entry.photoUrl || entry.extractedValue || hairPhoto
        if (photoUrl)
          answers.push({ questionIndex, photoUrl, text: null, tags: [] })
        return
      }

      const text = extractAnswerText(entry)
      const tags = extractTags(entry)

      if (text || tags.length > 0) {
        answers.push({ questionIndex, text, tags, photoUrl: null })
      }
    })

    return answers
  }

  async function fetchProductImage(
    productName,
    productCategory,
    productBrand,
    isMounted,
  ) {
    const encodedBrand = encodeURIComponent(productBrand)
    const encodedProduct = encodeURIComponent(productName)
    const primaryUrl = `${BLOB_BASE}/${encodedBrand}-${encodedProduct}.png?${SAS_TOKEN}`

    try {
      const res = await axios.get(primaryUrl)
      return isMounted ? res.config.url : null
    } catch {
      const encodedCat = encodeURIComponent(
        productCategory.replace(/'/g, '%27'),
      )
      const fallbackUrl = `${BLOB_BASE}/Placeholder-${encodedCat}.png?${SAS_TOKEN}`
      try {
        const catRes = await axios.get(fallbackUrl)
        return isMounted ? catRes.config.url : null
      } catch {
        return null
      }
    }
  }

  const displayProducts = hasProducts
    ? productRecommendations.product_name.map((name, i) => ({
        id: i,
        name,
        brand: productRecommendations.product_brand?.[i] ?? '',
        category: productRecommendations.product_category?.[i] ?? '',
        price: parseFloat(productRecommendations.price_filtered?.[i]) || 0,
        quantity: productRecommendations.product_quantity?.[i] ?? '',
        ingredients: productRecommendations.product_ingredients?.[i] ?? [],
        currency: '€',
        badge: 'Recommended',
        imageUrl: productImages[name] ?? null,
        productUrl: productRecommendations.product_url?.[i] ?? '',
        description: productRecommendations.product_description?.[i] ?? '',
      }))
    : []

  const total = displayProducts.length
  useEffect(() => {
    if (!hasProducts) return

    let isMounted = true
    setImagesLoading(true)

    const fetchAll = async () => {
      const images = {}
      const names = productRecommendations.product_name
      const brands = productRecommendations.product_brand
      const categories = productRecommendations.product_category

      for (let i = 0; i < names.length; i++) {
        const url = await fetchProductImage(
          names[i],
          categories[i],
          brands[i],
          isMounted,
        )
        if (url && isMounted) images[names[i]] = url
      }

      if (isMounted) {
        setProductImages(images)
        setImagesLoading(false)
      }
    }

    fetchAll()
    return () => {
      isMounted = false
    }
  }, [productRecommendations])

  const answers = buildAnswers()
  const routineEntry = buildRoutineEntry(routineAdviceText)

  // ── Build products from Redux productRecommendations ─────────────────────────
  const buildProducts = () => {
    const recs =
      productRecommendations?.price_filtered ||
      productRecommendations?.recommendations ||
      []
    if (!recs.length) return []

    return recs.slice(0, 6).map((p, i) => ({
      id: i + 1,
      name: p.product_name || p.name || 'Product',
      brand: p.brand || p.brand_name || 'Halisi Cosmetics',
      date: new Date().toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      }),
      badge: 'recommended',
      icon: FiDroplet,
    }))
  }

  const products = buildProducts()

  // ── Build diagnostics from diagnosticsLLMText ─────────────────────────────────
  const buildDiagnostics = () => {
    if (!diagnosticsLLMText) return []
    return [
      {
        id: 1,
        title: 'AI Hair Diagnostics',
        date: new Date().toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        }),
        issue: 'Hair Analysis',
        text: diagnosticsLLMText,
      },
    ]
  }

  const diagnostics = buildDiagnostics()
  const language = useSelector((state) => state.language)
  const translations = useSelector((state) => state.translationsData) || {}
  const getTranslationText = (
    keyPath,
    data = translations,
    lang = language,
  ) => {
    const validLangs = ['EN', 'FR', 'NL', 'SW']
    const normalizedLang = String(lang || 'EN')
      .trim()
      .toUpperCase()
    const selectedLang = validLangs.includes(normalizedLang)
      ? normalizedLang
      : 'EN'

    const keys = keyPath.split('.')
    let result = data
    for (const key of keys) {
      result = result?.[key]
      if (Array.isArray(result)) result = result[0]
    }

    if (result && typeof result === 'object') {
      return result[selectedLang] ?? result.EN ?? 'Text not found'
    }

    return result || 'Text not found'
  }

  const translateText = (keyPath, replacements = {}, fallback = '') => {
    let value = getTranslationText(keyPath)
    if (value === 'Text not found' || value === undefined || value === null) {
      value = fallback || keyPath
    }
    if (typeof value === 'string') {
      Object.entries(replacements).forEach(([key, replacement]) => {
        value = value.replace(
          new RegExp(`\\{${key}\\}`, 'g'),
          String(replacement),
        )
      })
    }
    return value
  }

  // ── Local UI state ───────────────────────────────────────────────────────────
  const routineRef = useRef(null)
  const diagRef = useRef(null)
  const kebabRefs = useRef({})

  const [activeTab, setActiveTab] = useState('overview')
  const [expanded, setExpanded] = useState(true)
  const [checklist, setChecklist] = useState(DEFAULT_CHECKLIST)
  const [openMenu, setOpenMenu] = useState(null)
  const [dayRecords, setDayRecords] = useState({})

  const doneCount = checklist.filter((c) => c.done).length
  const totalCount = checklist.length
  const progressPct = Math.round((doneCount / totalCount) * 100)

  const today = new Date()
  const [calYear, setCalYear] = useState(today.getFullYear())
  const [calMonth, setCalMonth] = useState(today.getMonth())

  const calendarDays = buildMonthCalendar(calYear, calMonth)
  const calendarWeeks = []
  for (let i = 0; i < calendarDays.length; i += 7)
    calendarWeeks.push(calendarDays.slice(i, i + 7))

  const MONTH_NAMES = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ]

  const getTodayStatus = () => {
    if (doneCount === 0) return 'today'
    if (doneCount === totalCount) return 'done'
    return 'partial'
  }

  const getDayStatus = (dayObj) => {
    if (!dayObj.day) return 'empty'
    if (dayObj.isFuture) return 'future'
    if (dayObj.isToday) return getTodayStatus()
    return dayRecords[dayObj.date] ?? 'none'
  }

  const prevMonth = () => {
    if (calMonth === 0) {
      setCalYear((y) => y - 1)
      setCalMonth(11)
    } else setCalMonth((m) => m - 1)
  }
  const nextMonth = () => {
    const isCurrent =
      calYear === today.getFullYear() && calMonth === today.getMonth()
    if (isCurrent) return
    if (calMonth === 11) {
      setCalYear((y) => y + 1)
      setCalMonth(0)
    } else setCalMonth((m) => m + 1)
  }

  const cycleDayStatus = (dayObj) => {
    if (dayObj.isFuture || dayObj.isToday) return
    const current = dayRecords[dayObj.date] ?? 'none'
    const next =
      current === 'none' ? 'done' : current === 'done' ? 'partial' : 'none'
    setDayRecords((prev) => ({ ...prev, [dayObj.date]: next }))
  }

  const calcStreak = () => {
    let streak = 0
    const check = new Date(today)
    check.setDate(check.getDate() - 1)
    while (true) {
      const key = check.toISOString().split('T')[0]
      const status = dayRecords[key]
      if (status === 'done' || status === 'partial') {
        streak++
        check.setDate(check.getDate() - 1)
      } else break
    }
    if (doneCount > 0) streak++
    return streak
  }

  const { canAccess } = usePlan()
  const [upgradeAlert, setUpgradeAlert] = useState({
    visible: false,
    minPlan: null,
  })

  const photoAnswer = answers.find((a) => a.questionIndex === 2 && a.photoUrl)

  const toggleCheck = (id) =>
    setChecklist((prev) =>
      prev.map((c) => (c.id === id ? { ...c, done: !c.done } : c)),
    )

  const initials = userName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

  const trackerClass = (status) =>
    ({
      done: 'tday-done',
      partial: 'tday-partial',
      none: 'tday-none',
      today: 'tday-today',
      future: 'tday-future',
    })[status] || 'tday-none'

  const promptTypeClass = (type) =>
    ({
      reminder: 'ptype-reminder',
      tip: 'ptype-tip',
      suggestion: 'ptype-suggestion',
    })[type] || 'ptype-reminder'
  const promptTypeLabel = (type) =>
    ({
      reminder: translateText('userProfile.sections.reminder', {}, 'Reminder'),
      tip: translateText('userProfile.sections.tip', {}, 'Tip'),
      suggestion: translateText(
        'userProfile.sections.suggestion',
        {},
        'Suggestion',
      ),
    })[type] || type

  // ── Empty state check ─────────────────────────────────────────────────────────
  const hasRealData = collectedAnswers.length > 0

  const base64Header = 'data:image/png;base64,'

  const rawSegmentedImage = `${base64Header}${segmentedImageRaw}`
  return (
    <>
      <div className='up-root'>
        {/* ── HEADER ── */}
        <div className='up-header'>
          <div className='up-header-left'>
            {onBack && (
              <button className='up-back' onClick={onBack}>
                ←
              </button>
            )}
            <span className='up-header-title'>
              {translateText('userProfile.headerTitle')}
            </span>
          </div>
        </div>

        {/* ── BANNER ── */}
        <div className='up-banner'>
          <div className='up-banner-top'>
            <div className='up-avatar-wrap'>
              {userAvatar ? (
                <img src={userAvatar} alt={userName} className='up-avatar' />
              ) : (
                <div className='up-avatar-initials'>{initials}</div>
              )}
              {(photoAnswer?.photoUrl || hairPhoto) && (
                <img
                  src={rawSegmentedImage || hairPhoto}
                  alt='Hair'
                  className='up-hair-thumb'
                />
              )}
            </div>
            <div className='up-banner-info'>
              <div className='up-banner-name'>{userName}</div>
              <div className='up-banner-sub'>
                {translateText('userProfile.bannerSub', {
                  answered: answers.length,
                  total: QUESTION_META.length,
                })}
              </div>
            </div>
          </div>
          {hairType && hairType !== '—' && (
            <div className='up-hair-pill'>
              <span className='up-hair-pill-dot' />
              {translateText('userProfile.hairTypeDetected', { hairType })}
            </div>
          )}
        </div>

        {/* ── STATS ── */}
        <div className='up-stats'>
          <div className='up-stat'>
            <div className='up-stat-num'>{doneCount}</div>
            <div className='up-stat-label'>
              {translateText('userProfile.stats.routinesDoneToday')}
            </div>
          </div>
          <div className='up-stat'>
            <div className='up-stat-num'>{products.length}</div>
            <div className='up-stat-label'>
              {translateText('userProfile.stats.productsRecommended')}
            </div>
          </div>
          <div className='up-stat'>
            <div className='up-stat-num'>{diagnostics.length}</div>
            <div className='up-stat-label'>
              {translateText('userProfile.stats.diagnosticsRun')}
            </div>
          </div>
        </div>

        {/* ── TABS ── */}
        <div className='up-tabs'>
          {TABS.map((tab) => {
            const TAB_FEATURE_MAP = {
              overview: 'user_profile',
              products: 'product_recommendation',
              diagnostics: 'hair_diagnostics',
              routine: 'hair_routine',
              tracker: 'hair_tracker',
            }
            const requiredFeature = TAB_FEATURE_MAP[tab.id]
            return (
              <button
                key={tab.id}
                className={`up-tab${activeTab === tab.id ? ' active' : ''}`}
                onClick={() => {
                  if (!canAccess(requiredFeature)) {
                    setUpgradeAlert({
                      visible: true,
                      minPlan: (() => {
                        const minPlanId =
                          requiredFeature === 'hair_routine' ? 'pro' : 'care'
                        const plan = getPlanById(minPlanId)
                        return `${plan.name} (${plan.tier})`
                      })(),
                    })
                    return
                  }
                  setActiveTab(tab.id)
                }}
              >
                {translateText(`userProfile.tabs.${tab.key}`)}
              </button>
            )
          })}
        </div>

        {upgradeAlert.visible && (
          <AlertMessage
            message={translateText('userProfile.upgradeAlert.message')}
            subtext={translateText('userProfile.upgradeAlert.subtext', {
              plan: upgradeAlert.minPlan,
            })}
            confirmButtonText={translateText('userProfile.upgradeAlert.button')}
            onConfirm={() => {
              setUpgradeAlert({ visible: false, minPlan: null })
              goToPricing?.()
            }}
            onCancel={() => setUpgradeAlert({ visible: false, minPlan: null })}
          />
        )}

        {/* ════ TAB: OVERVIEW ════ */}
        {activeTab === 'overview' && (
          <>
            <div className='up-section-label'>
              {translateText('userProfile.sections.hairInformation')}
            </div>
            <div className='up-card'>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 16px 0',
                }}
              >
                <span
                  style={{
                    fontSize: '10px',
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: 'var(--muted)',
                  }}
                >
                  {translateText('userProfile.sections.yourAnswers')}
                </span>
                <button
                  onClick={() => setExpanded((v) => !v)}
                  style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: 'var(--brand)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  {expanded
                    ? `▲ ${translateText('userProfile.sections.collapse')}`
                    : `▼ ${translateText('userProfile.sections.expand')}`}
                </button>
              </div>

              {expanded && (
                <div style={{ paddingTop: 6 }}>
                  {!hasRealData ? (
                    <div className='up-empty'>
                      <div className='up-empty-icon'>💬</div>
                      {translateText('userProfile.sections.completeQuizPrompt')}
                    </div>
                  ) : answers.length === 0 ? (
                    <div className='up-empty'>
                      <div className='up-empty-icon'>💬</div>
                      {translateText('userProfile.sections.noAnswersRecorded')}
                    </div>
                  ) : (
                    answers.map((answer, i) => {
                      const meta = QUESTION_META[answer.questionIndex] ?? {
                        icon: FiMessageCircle,
                        label: 'Answer',
                      }
                      return (
                        <div
                          className='up-info-row'
                          key={i}
                          style={{ animationDelay: `${i * 0.06}s` }}
                        >
                          <div className='up-info-icon'>
                            <meta.icon size={14} />
                          </div>
                          <div className='up-info-body'>
                            <div className='up-info-q'>
                              {meta.key
                                ? translateText(
                                    `userProfile.questionMeta.${meta.key}`,
                                  )
                                : 'Answer'}
                            </div>
                            {answer.text && (
                              <div className='up-info-a'>{answer.text}</div>
                            )}
                            {answer.photoUrl && answer.questionIndex === 2 && (
                              <img
                                src={rawSegmentedImage}
                                alt='Hair'
                                className='up-hair-photo'
                              />
                            )}
                            {answer.tags?.length > 0 && (
                              <div className='up-tag-row'>
                                {answer.tags.map((tag, j) => (
                                  <span key={j} className='up-tag'>
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    })
                  )}
                </div>
              )}

              <div className='up-complete-footer'>
                <div className='up-footer-note'>
                  <strong>Halisi</strong>{' '}
                  {translateText('userProfile.sections.profileSummary')}
                  <br />
                  {translateText('userProfile.sections.personalisedReady')}
                </div>
                <div className='up-complete-badge'>
                  <span className='up-complete-dot' />
                  {hasRealData
                    ? translateText('userProfile.sections.profileComplete')
                    : translateText('userProfile.sections.completeQuizInChat')}
                </div>
              </div>
            </div>
          </>
        )}

        {/* ════ TAB: PRODUCTS ════ */}
        {activeTab === 'products' && (
          <>
            <div className='up-section-label'>Product recommendations</div>
            <div className='up-card'>
              {total === 0 ? (
                <div className='up-empty'>
                  <div className='up-empty-icon'>🛍️</div>
                  No products recommended yet. Complete the hair quiz in the
                  chat to get personalised picks.
                </div>
              ) : (
                displayProducts.map((p, i) => (
                  <div
                    className='up-product-item'
                    key={p.id}
                    style={{ animationDelay: `${i * 0.07}s` }}
                  >
                    <div className='up-product-img'>
                      {p.imageUrl ? (
                        <img
                          className='up-product-thumb'
                          src={p.imageUrl}
                          alt={p.name}
                        />
                      ) : (
                        <FiPackage size={15} />
                      )}
                    </div>
                    <div className='up-product-info'>
                      <div className='up-product-name'>{p.name}</div>
                      <div className='up-product-brand'>{p.brand}</div>
                      <div className='up-product-date'>
                        {p.category || 'Recommended'} ·{' '}
                        {p.quantity || 'Personalised pick'}
                      </div>
                    </div>
                    <div className='up-product-badge badge-recommended'>
                      Recommended
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}

        {/* ════ TAB: DIAGNOSTICS ════ */}
        {activeTab === 'diagnostics' && (
          <>
            <div className='up-section-label'>Diagnostics history</div>
            <div className='up-card' style={{ overflow: 'visible' }}>
              {diagnostics.length === 0 ? (
                <div className='up-empty'>
                  <div className='up-empty-icon'>🔬</div>
                  No diagnostics yet. Complete the full hair consultation in the
                  chat to see your AI diagnostics here.
                </div>
              ) : (
                diagnostics.map((d, i) => (
                  <div
                    key={d.id}
                    className='up-diag-item'
                    style={{
                      animationDelay: `${i * 0.07}s`,
                      position: 'relative',
                      zIndex: openMenu === `diag-${d.id}` ? 50 : 1,
                      overflow: 'visible',
                    }}
                  >
                    <div className='up-diag-header'>
                      <div className='up-diag-title'>{d.title}</div>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                        }}
                      >
                        <div className='up-diag-date'>{d.date}</div>
                        <div className='up-menu-wrap'>
                          <button
                            ref={(el) =>
                              (kebabRefs.current[`diag-${d.id}`] = el)
                            }
                            className='up-kebab'
                            onClick={(e) => {
                              e.stopPropagation()
                              setOpenMenu((prev) =>
                                prev === `diag-${d.id}` ? null : `diag-${d.id}`,
                              )
                            }}
                          >
                            ⋯
                          </button>
                          {openMenu === `diag-${d.id}` && (
                            <>
                              <div
                                style={{
                                  position: 'fixed',
                                  inset: 0,
                                  zIndex: 199,
                                }}
                                onClick={() => setOpenMenu(null)}
                              />
                              <div
                                onClick={(e) => e.stopPropagation()}
                                style={{ position: 'relative', zIndex: 300 }}
                              >
                                <ShareButtons card='diagnostics' />
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className='up-diag-issue'>{d.issue}</div>
                    <div className='up-diag-text'>{d.text}</div>
                  </div>
                ))
              )}
            </div>
          </>
        )}

        {/* ════ TAB: ROUTINE ════ */}
        {activeTab === 'routine' && (
          <FeatureGate feature='hair_routine' onUpgrade={() => goToPricing()}>
            <>
              <div className='up-section-label'>Hair routine advice</div>
              <div className='up-card'>
                {routineAdviceReady && routineEntry ? (
                  <div className='up-routine-shell'>
                    <div
                      className='up-routine-hero'
                      style={{ position: 'relative' }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          gap: 12,
                        }}
                      >
                        <div>
                          <div className='up-routine-hero-badge'>
                            Personalised routine
                          </div>
                          <div className='up-routine-hero-title'>
                            Your hair care plan
                          </div>
                        </div>
                        <div className='up-menu-wrap'>
                          <button
                            className='up-kebab'
                            onClick={(e) => {
                              e.stopPropagation()
                              setOpenMenu((prev) =>
                                prev === 'routine' ? null : 'routine',
                              )
                            }}
                          >
                            ⋯
                          </button>
                          {openMenu === 'routine' && (
                            <>
                              <div
                                style={{
                                  position: 'fixed',
                                  inset: 0,
                                  zIndex: 199,
                                }}
                                onClick={() => setOpenMenu(null)}
                              />
                              <div
                                onClick={(e) => e.stopPropagation()}
                                style={{ position: 'relative', zIndex: 300 }}
                              >
                                <ShareButtons card='routine' />
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                      <div className='up-routine-hero-copy'>
                        These steps were generated from your hair profile and
                        product recommendations so they’re easier to follow.
                      </div>
                    </div>
                    <div className='up-routine-accordion'>
                      <RoutineEntry entry={routineEntry} defaultOpen={true} />
                    </div>
                  </div>
                ) : (
                  <div className='up-empty'>
                    <div className='up-empty-icon'>🌿</div>
                    Complete the hair consultation in the chat to receive your
                    personalised routine advice.
                  </div>
                )}
              </div>
            </>
          </FeatureGate>
        )}

        {/* ════ TAB: TRACKER ════ */}
        {activeTab === 'tracker' && (
          <>
            <div className='up-section-label'>Today's routine checklist</div>
            <div className='up-card'>
              <div className='up-checklist-header'>
                <div className='up-checklist-title'>Hair care tasks</div>
                <div className='up-checklist-progress'>
                  {doneCount} / {totalCount} done
                </div>
              </div>
              <div className='up-progress-bar-wrap'>
                <div
                  className='up-progress-bar-fill'
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <FeatureGate
                feature='hair_tracker'
                onUpgrade={() => goToPricing()}
              >
                <>
                  {checklist.map((item, i) => (
                    <div
                      className='up-check-item'
                      key={item.id}
                      onClick={() => toggleCheck(item.id)}
                      style={{ animationDelay: `${i * 0.04}s` }}
                    >
                      <div
                        className={`up-checkbox${item.done ? ' checked' : ''}`}
                      >
                        {item.done && (
                          <span className='up-checkbox-tick'>✓</span>
                        )}
                      </div>
                      <div className='up-check-body'>
                        <div
                          className={`up-check-label${item.done ? ' done' : ''}`}
                        >
                          {item.label}
                        </div>
                        <div className='up-check-sub'>{item.sub}</div>
                      </div>
                      <div className='up-check-badge'>{item.freq}</div>
                    </div>
                  ))}
                  {doneCount === totalCount && (
                    <div
                      style={{
                        padding: '14px 16px',
                        textAlign: 'center',
                        fontSize: '13px',
                        color: 'var(--green)',
                        fontWeight: 600,
                        borderTop: '1px solid var(--border)',
                      }}
                    >
                      Amazing! Full routine complete for today. 🎉
                    </div>
                  )}
                </>
              </FeatureGate>
            </div>

            {/* Calendar tracker */}
            <div className='up-section-label'>Habit tracker</div>
            <div className='up-card'>
              <div className='up-tracker-month'>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <button
                    onClick={prevMonth}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: 16,
                      color: 'var(--brand)',
                      padding: '0 4px',
                      lineHeight: 1,
                    }}
                  >
                    ‹
                  </button>
                  <div className='up-tracker-month-label'>
                    {MONTH_NAMES[calMonth]} {calYear}
                  </div>
                  <button
                    onClick={nextMonth}
                    disabled={
                      calYear === today.getFullYear() &&
                      calMonth === today.getMonth()
                    }
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: 16,
                      color: 'var(--brand)',
                      padding: '0 4px',
                      lineHeight: 1,
                      opacity:
                        calYear === today.getFullYear() &&
                        calMonth === today.getMonth()
                          ? 0.25
                          : 1,
                    }}
                  >
                    ›
                  </button>
                </div>
                {calcStreak() > 0 && (
                  <div className='up-streak-badge'>
                    🔥 {calcStreak()}-day streak
                  </div>
                )}
              </div>

              <div className='up-tracker-week-labels'>
                {WEEK_DAYS.map((d, i) => (
                  <div className='up-week-label' key={i}>
                    {d}
                  </div>
                ))}
              </div>

              {calendarWeeks.map((week, wi) => (
                <div
                  className='up-tracker-grid'
                  key={wi}
                  style={{
                    paddingBottom:
                      wi < calendarWeeks.length - 1 ? '4px' : '14px',
                  }}
                >
                  {week.map((dayObj, di) => {
                    const status = getDayStatus(dayObj)
                    return (
                      <div
                        key={di}
                        className={`up-tracker-day${dayObj.day ? ` ${trackerClass(status)}` : ''}`}
                        onClick={() => dayObj.day && cycleDayStatus(dayObj)}
                        style={{
                          cursor: dayObj.isPast ? 'pointer' : 'default',
                          visibility: dayObj.day ? 'visible' : 'hidden',
                        }}
                      >
                        {dayObj.day && (
                          <span
                            style={{
                              fontSize: 9,
                              opacity: 0.6,
                              position: 'absolute',
                              top: 2,
                              right: 4,
                            }}
                          >
                            {status === 'done'
                              ? '✓'
                              : status === 'partial'
                                ? '~'
                                : status === 'today'
                                  ? '•'
                                  : dayObj.day}
                          </span>
                        )}
                        {status === 'empty' ||
                        !['done', 'partial', 'today'].includes(status)
                          ? dayObj.day
                          : ''}
                      </div>
                    )
                  })}
                </div>
              ))}

              <div className='up-tracker-legend'>
                <div className='up-legend-item'>
                  <div
                    className='up-legend-dot'
                    style={{ background: 'var(--brand)' }}
                  />
                  Completed
                </div>
                <div className='up-legend-item'>
                  <div
                    className='up-legend-dot'
                    style={{
                      background: 'rgba(212,165,165,0.5)',
                      border: '1px solid var(--border-warm)',
                    }}
                  />
                  Partial
                </div>
                <div className='up-legend-item'>
                  <div
                    className='up-legend-dot'
                    style={{
                      background: '#fdf0f4',
                      border: '1px solid var(--border)',
                    }}
                  />
                  Skipped
                </div>
                <div className='up-legend-item'>
                  <div
                    className='up-legend-dot'
                    style={{ background: 'var(--dark)' }}
                  />
                  Today
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  )
}
